<script setup lang="ts">
import { DownloadIcon } from "@heroicons/vue/outline";
import { useLazyQuery } from "@vue/apollo-composable";
import { watchOnce } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type {
	AdminCo2XlsxExportQuery,
	AdminCo2XlsxExportQueryVariables,
	AdminRawReportQuery,
	TmReportsCarboneReportQuery,
	TmReportsCarboneReportQueryVariables,
	TmReportsPdfReportQuery,
	TmReportsPdfReportQueryVariables,
} from "@/__generated__/types";
import { PdfReportTypeEnum } from "@/__generated__/types";
import MlCard from "@/components/molecules/MlCard.vue";
import { usePdfExport } from "@/components/pages/PgStatistics/PgResults/composables";
import dayjs from "@/lib/dayjs/config";
import useCurrentUser from "@/utils/composables/useCurrentUser/useCurrentUser";
import useEmissionFactorsCsv from "@/utils/composables/useEmissionFactorsCsv/useEmissionFactorsCsv";
import generateReport from "@/utils/helpers/generateReport";
import saveFile from "@/utils/helpers/saveFile";
import CO2_XLSX_EXPORT from "./Co2XlsxExport.query";
import CSV_REPORT_QUERY from "./CsvReports.query";
import OgReportRequestModal from "./OgReportRequestModal.vue";
import TM_REPORTS_CARBONE_REPORT_QUERY from "./TmReportsCarboneReport.query";
import TM_REPORTS_PDF_REPORT_QUERY from "./TmReportsPDFReport.query";
import { ReportType } from "./types";

const { t } = useI18n();
const { currentUser } = useCurrentUser();

const { loading: isPdfLoading, exportFile: exportAsPdfFile } = usePdfExport();

const emissionFactorsCsv = useEmissionFactorsCsv();

const {
	result: resultRawReport,
	loading: loadingRawReport,
	load: fetchRawReport,
	error: errorRawReport,
	refetch: refetchRawReport,
} = useLazyQuery<AdminRawReportQuery>(CSV_REPORT_QUERY, null, {
	fetchPolicy: "network-only",
});

const {
	result: resultCo2XlsxExport,
	loading: loadingCo2XlsxExport,
	load: fetchCo2XlsxExport,
	error: errorCo2XlsxExport,
	refetch: refetchCo2XlsxExport,
} = useLazyQuery<AdminCo2XlsxExportQuery>(CO2_XLSX_EXPORT, null, {
	fetchPolicy: "network-only",
});

const pdfReportQuery = useLazyQuery<TmReportsPdfReportQuery>(
	TM_REPORTS_PDF_REPORT_QUERY,
	undefined,
	{
		fetchPolicy: "network-only",
	},
);

const carboneReportQuery = useLazyQuery<TmReportsCarboneReportQuery>(
	TM_REPORTS_CARBONE_REPORT_QUERY,
	undefined,
	{
		fetchPolicy: "network-only",
	},
);

watch(loadingRawReport, () => {
	if (
		loadingRawReport.value ||
		!resultRawReport.value?.getDataPointsEncodedReport
	)
		return;

	generateReport(
		resultRawReport.value.getDataPointsEncodedReport,
		currentUser.value?.lastName,
		"xlsx",
	);
});

const allReportTypes = computed(() => [
	{
		title: t("CO2 Footprint Report"),
		type: ReportType.CO2,
		description: t(
			"Get your company’s carbon footprint report covering scope 1, scope 2 and scope 3 emissions.",
		),
		buttonIcon: DownloadIcon,
		buttonText: t("Download CO2 report"),
		action: openCustomReportModal,
		loading: pdfReportQuery.loading.value,
	},
	{
		title: t("Emission Factors"),
		type: ReportType.EMISSION_FACTORS,
		description: t(
			"Get the complete list of emission factors that are used to calculate the CO2 footprint of your company.",
		),
		buttonIcon: DownloadIcon,
		buttonText: t("Download emission factors"),
		action: () => emissionFactorsCsv.download(),
		loading: emissionFactorsCsv.loading.value,
	},
	{
		title: t("Custom questionnaires"),
		type: ReportType.CUSTOM,
		description: t(
			"If you created custom questionnaires, this is where you can download your results.",
		),
		buttonText: t("Download export"),
		action: openCustomReportModal,
		loading: loadingRawReport.value && isModalRevealed.value,
	},
	{
		title: t("VSME Report"),
		type: ReportType.VSME,
		description: t(
			"Get a holistic report based on the VSME reporting standard.",
		),
		buttonText: t("Download export"),
		action: openCustomReportModal,
		loading: loadingRawReport.value && isModalRevealed.value,
	},
	{
		title: t("ESRS Report"),
		type: ReportType.ESRS,
		description: t(
			"Get a holistic report based on the ESRS reporting standard.",
		),
		buttonText: t("Download export"),
		action: openCustomReportModal,
		loading: loadingRawReport.value && isModalRevealed.value,
	},
	{
		title: t("All platform data"),
		type: ReportType.ALL,
		description: t(
			"Download all platform data. This is possible only in the .xlxs file.",
		),
		buttonText: t("Download export"),
		action: () => fetchRawReport(undefined) || refetchRawReport({}),
		loading: loadingRawReport.value && isModalRevealed.value,
	},
]);

const modalType = ref("");
const isModalRevealed = ref(false);

function openCustomReportModal(type: ReportType) {
	modalType.value = type;
	isModalRevealed.value = true;
}

async function initiateCarboneExport(
	from: Date,
	to: Date,
	locationIds: string[],
	type: string,
) {
	watchOnce(carboneReportQuery.result, () => {
		const rawReportData =
			carboneReportQuery.result.value?.getSingleProjectExport;

		const company = currentUser.value?.entity.name ?? "";
		const date = dayjs().format("DD-MM-YYYY");
		let name = `${company}_export_${date}`;
		let contentType = "";

		switch (type) {
			case "word":
				name += ".docx";
				contentType =
					"application/vnd.openxmlformats-officedocument.wordprocessingml.document";
				break;
			case "pdf_beta":
				name += ".pdf";
				contentType = "application/pdf";
				break;
		}

		if (rawReportData) {
			saveFile(rawReportData, name, contentType);
		}
	});

	let backendType = "";
	switch (type) {
		case "pdf_beta":
			backendType = "pdf";
			break;
		case "word":
			backendType = "docx";
			break;
	}

	const variables: TmReportsCarboneReportQueryVariables = {
		dateFrom: from,
		dateTo: to,
		locationIds,
		type: backendType,
	};

	carboneReportQuery.load(undefined, variables) ||
		carboneReportQuery.refetch(variables);
}

async function initiateCO2ReportDownload(
	from: Date,
	to: Date,
	workspaceIds?: string[],
	type: "pdf" | "xlsx" = "pdf",
) {
	switch (type) {
		case "pdf": {
			watchOnce(pdfReportQuery.result, () => {
				const pdfData = pdfReportQuery.result.value?.downloadPDFReport;

				const company = currentUser.value?.entity.name ?? "";
				const date = dayjs().format("DD-MM-YYYY");
				const name = `${company}_co2_report_${date}.pdf`;

				if (pdfData) {
					saveFile(pdfData, name, "application/pdf");
				}
			});

			const variables: TmReportsPdfReportQueryVariables = {
				reportTypes: [PdfReportTypeEnum.CARBON_FOOTPRINT],
				workspaceIds: workspaceIds ? workspaceIds : undefined,
				dateFrom: from,
				dateTo: to,
			};

			pdfReportQuery.load(undefined, variables) ||
				pdfReportQuery.refetch(variables);
			break;
		}

		case "xlsx": {
			watchOnce(resultCo2XlsxExport, () => {
				const pdfData = resultCo2XlsxExport.value?.getDatapointsAsXlsxAdmin;

				const company = currentUser.value?.entity.name ?? "";
				const date = dayjs().format("DD-MM-YYYY");
				const name = `${company}_co2_report_${date}.xlsx`;

				if (pdfData) {
					saveFile(
						pdfData,
						name,
						"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					);
				}
			});

			const variables: AdminCo2XlsxExportQueryVariables = {
				workspaceIds: workspaceIds ? workspaceIds : undefined,
				dateFrom: from,
				dateTo: to,
			};

			fetchCo2XlsxExport(undefined, variables) ||
				refetchCo2XlsxExport(variables);

			break;
		}
	}
}

function closeModal() {
	isModalRevealed.value = false;
}

const handleModalSubmit = (e: {
	type: string;
	dateRange: { dateFrom: Date; dateTo: Date };
	workspaces?: string[];
	locatiosn?: string[];
}) => {
	if (["pdf_beta", "word"].includes(e.type) && e.locations) {
		initiateCarboneExport(
			e.dateRange.dateFrom,
			e.dateRange.dateTo,
			e.locations,
			e.type,
		);
		return;
	}

	if (modalType.value === ReportType.CO2) {
		initiateCO2ReportDownload(
			e.dateRange.dateFrom,
			e.dateRange.dateTo,
			e.workspaces,
			e.type,
		);
		return;
	}

	switch (e.type) {
		case "pdf":
			exportAsPdfFile(
				PdfReportTypeEnum.ALL,
				[],
				e.dateRange.dateFrom.toString(),
				e.dateRange.dateTo.toString(),
				[],
				undefined,
				e.workspaces,
				undefined,
				[modalType.value],
			);

			break;

		case "xlsx": {
			const vars = {
				entityIds: e.workspaces?.length
					? e.workspaces
					: currentUser.value?.entities.map((e) => e._id),
				dateFrom: e.dateRange.dateFrom.toString(),
				dateTo: e.dateRange.dateTo.toString(),
				reportingFrameworks: [modalType.value],
			};

			fetchRawReport(undefined, vars) || refetchRawReport(vars);

			break;
		}
	}
};
</script>

<template>
  <div class="pt-5 px-6 grid grid-cols-1 gap-6 xl:grid-cols-5">
    <div class="xl:col-span-4">
      <h2 class="text-lg font-medium leading-6 text-gray-900 sm:truncate mb-3">
        {{ t('Reports') }}
      </h2>
      <p class="mb-10 text-gray-500 text-sm">
        {{ t('Request or download your relevant sustainability reports.') }}
      </p>
      <div class="mb-12 gap-6 grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1">
        <MlCard
          v-for="reportType in allReportTypes"
          :key="reportType.type"
          :title="reportType.title"
          :description="reportType.description"
          :buttonIcon="reportType.buttonIcon"
          :buttonText="reportType.buttonText"
          :loading="reportType.loading"
          @buttonClick="reportType.action(reportType.type)"
        />
      </div>
    </div>

    <OgReportRequestModal
      :isRevealed="isModalRevealed"
      class="min-w-[700px] gap-10 px-10"
      @closeModal="closeModal"
      @submit="handleModalSubmit"
      :allow-word-export="true"
      :allowPdfBetaExport="true"
    />
  </div>
</template>
